import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { LiveBadge } from "@/components/pakistan/LiveBadge";
import { PakistanNavigator, PakistanBreadcrumbSchema } from "@/components/pakistan/PakistanNavigator";
import { FaqSection } from "@/components/FaqSection";
import {
  computeCityPrices,
  findCity,
  formatMoney,
  GRAMS_PER_TOLA,
} from "@/lib/gold-data";
import { goldRatesQueryOptions, useGoldRates } from "@/lib/nonblocking-rates";
import { SITE_URL } from "@/lib/seo";
import { urduCityName } from "@/lib/pakistan-city-urdu";
import { getPakistanCityMeta } from "@/lib/pakistan-city-data";

/**
 * /pakistan/urdu/[city] — Urdu (Nastaliq) mirror of the short-form
 * Pakistan city page. Same live prices, RTL layout, hreflang-linked
 * with /pakistan/[city] (English).
 */
export const Route = createFileRoute("/pakistan/urdu/$city")({
  loader: ({ context, params }) => {
    const c = findCity("pakistan", params.city);
    if (!c) throw notFound();
    void context.queryClient.prefetchQuery(goldRatesQueryOptions);
  },
  head: ({ params }) => {
    const c = findCity("pakistan", params.city);
    const name = c?.name ?? "City";
    const ur = urduCityName(params.city, name);
    const url = `${SITE_URL}/pakistan/urdu/${params.city}`;
    const enUrl = `${SITE_URL}/pakistan/${params.city}`;
    return {
      meta: [
        { title: `${ur} میں آج سونے کا ریٹ — Gold Rate ${name} (Urdu)` },
        {
          name: "description",
          content: `${ur} میں 24K، 22K اور 21K سونے کا لائیو ریٹ۔ ہر 10 منٹ میں انٹرنیشنل سپاٹ اور USD/PKR سے اپ ڈیٹ۔`,
        },
        { property: "og:title", content: `${ur} میں آج سونے کا ریٹ` },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hreflang: "ur-PK", href: url },
        { rel: "alternate", hreflang: "en-PK", href: enUrl },
        { rel: "alternate", hreflang: "x-default", href: enUrl },
      ],
    };
  },
  component: PakistanUrduCityPage,
  notFoundComponent: () => (
    <Layout>
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="text-2xl font-bold">شہر نہیں ملا</h1>
        <Link to="/pakistan/urdu" className="mt-4 inline-block text-primary underline">
          پاکستان اردو ہب پر جائیں
        </Link>
      </div>
    </Layout>
  ),
});

function PakistanUrduCityPage() {
  const { city: slug } = Route.useParams();
  const { data } = useGoldRates();
  const city = findCity("pakistan", slug)!;
  const meta = getPakistanCityMeta(slug);
  const ur = urduCityName(slug, city.name);
  const prices = computeCityPrices({
    spotUsdPerOz: data.spotUsdPerOz,
    fxRate: data.pkrPerUsd,
    country: "pakistan",
    cityPremium: city.premium,
  });

  return (
    <Layout>
      <section
        dir="rtl"
        lang="ur"
        className="mx-auto max-w-4xl px-4 py-8"
        style={{ fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', system-ui, serif" }}
      >
        <nav className="text-xs text-muted-foreground mb-3" dir="ltr">
          <Link to="/" className="hover:underline">Home</Link> ›{" "}
          <Link to="/pakistan" className="hover:underline">Pakistan</Link> ›{" "}
          <Link to="/pakistan/urdu" className="hover:underline">اردو</Link> › {city.name}
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold leading-relaxed">
          {ur} میں آج سونے کا ریٹ
        </h1>
        <p className="mt-3 text-lg leading-loose text-muted-foreground">
          {ur} میں 24، 22 اور 21 قیراط سونے کی تازہ ترین قیمت۔ ہر 10 منٹ میں
          انٹرنیشنل سپاٹ (XAU/USD) اور USD/PKR ایکسچینج ریٹ سے خودکار اپ ڈیٹ۔
        </p>
        <div className="mt-4" dir="ltr">
          <LiveBadge label={`${city.name} Live`} />
        </div>

        <div className="mt-6 rounded-xl border border-primary/30 bg-primary/5 p-4 leading-loose">
          <p>
            آج {ur} میں 24 قیراط سونے کی قیمت{" "}
            <strong dir="ltr">{formatMoney(prices.perTola24, "PKR")}</strong> فی تولہ ہے۔
            22 قیراط <strong dir="ltr">{formatMoney(prices.perTola22, "PKR")}</strong> اور 21
            قیراط <strong dir="ltr">{formatMoney(prices.perTola21, "PKR")}</strong>۔
          </p>
        </div>

        <div className="mt-6 overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm" dir="ltr">
            <thead className="bg-muted/60 text-left">
              <tr>
                <th className="px-4 py-3">Unit</th>
                <th className="px-4 py-3 text-right">24K</th>
                <th className="px-4 py-3 text-right">22K</th>
                <th className="px-4 py-3 text-right">21K</th>
              </tr>
            </thead>
            <tbody>
              {[
                { u: "Per Gram", k24: prices.perGram24, k22: prices.perGram22, k21: prices.perGram21 },
                { u: `Per Tola (${GRAMS_PER_TOLA}g)`, k24: prices.perTola24, k22: prices.perTola22, k21: prices.perTola21 },
                { u: "Per 10 Grams", k24: prices.per10g24, k22: prices.per10g22, k21: prices.per10g21 },
              ].map((r) => (
                <tr key={r.u} className="border-t border-border">
                  <th scope="row" className="px-4 py-3 text-left font-medium">{r.u}</th>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold">{formatMoney(r.k24, "PKR")}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatMoney(r.k22, "PKR")}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatMoney(r.k21, "PKR")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {meta && (
          <article className="mt-8 leading-loose text-foreground/90">
            <h2 className="text-xl font-bold mb-3">{ur} کے سرافہ بازار کے بارے میں</h2>
            <p>
              {ur} کا سب سے بڑا سرافہ مارکیٹ <strong dir="ltr">{meta.sarafa}</strong> ہے۔
              زیادہ تر جیولری کی دکانیں{" "}
              <span dir="ltr">{meta.jewellerHubs.join("، ")}</span> کے علاقوں میں ہیں۔ یہاں
              <strong> {meta.preferredKarat.toUpperCase()}</strong> سونا سب سے زیادہ فروخت ہوتا
              ہے۔
            </p>
            <p className="mt-3">
              میکنگ چارجز عموماً 8% سے 20% کے درمیان ہوتے ہیں (ڈیزائن کی پیچیدگی پر منحصر)۔
              زیورات کی خریداری پر 17% GST بھی لاگو ہوتی ہے۔ ہمیشہ تحریری رسید اور پیوریٹی
              سرٹیفکیٹ ضرور لیں۔
            </p>
          </article>
        )}

        <FaqSection
          faqs={[
            {
              q: `${ur} میں آج 24 قیراط سونے کا ریٹ کیا ہے؟`,
              a: `${ur} میں آج 24 قیراط سونا ${formatMoney(prices.perTola24, "PKR")} فی تولہ اور ${formatMoney(prices.perGram24, "PKR")} فی گرام ہے۔`,
            },
            {
              q: `${ur} اور کراچی کے ریٹ میں کیا فرق ہے؟`,
              a: "قومی حوالہ جاتی ریٹ ایک ہی ہے۔ ہر شہر کا مقامی پریمیم مختلف ہوتا ہے (0.05% سے 0.16% تک) جو ٹرانسپورٹ اور ڈیلر مارجن کی وجہ سے بنتا ہے۔",
            },
            {
              q: "کیا یہ ریٹ ہر روز بدلتا ہے؟",
              a: "جی ہاں۔ ہر 10 منٹ میں انٹرنیشنل سپاٹ اور USD/PKR سے دوبارہ حساب لگایا جاتا ہے۔",
            },
          ]}
          title="اکثر پوچھے جانے والے سوالات"
        />

        <div className="mt-6" dir="ltr">
          <Link to="/$country/$city" params={{ country: "pakistan", city: slug }} className="text-primary hover:underline text-sm">
            English version — Gold rate in {city.name} →
          </Link>
        </div>
        <div dir="ltr">
          <PakistanBreadcrumbSchema
            items={[
              { name: "Pakistan", href: "https://drhint.com/pakistan" },
              { name: "اردو", href: "https://drhint.com/pakistan/urdu" },
              { name: city.name, href: `https://drhint.com/pakistan/urdu/${slug}` },
            ]}
          />
          <PakistanNavigator />
        </div>
      </section>
    </Layout>
  );
}